import {
  cloneElement,
  createElement,
  CSSProperties,
  forwardRef,
  KeyboardEvent,
  ReactElement,
  ReactNode,
} from "react";
import cx from "classnames";
import styles from "./FlexLayoutItem.module.css";
import {
  flexLayout,
  layouts as flexLayoutLayouts,
} from "./FlexLayout.component";
import type {
  Align as FLAlign,
  Direction,
  Justify,
  Wrap,
} from "./FlexLayout.component";

export type Align = "auto" | "start" | "center" | "end" | "stretch";
export type Basis =
  | number
  | string
  | "auto"
  | "fill"
  | "max-content"
  | "min-content"
  | "fit-content"
  | "content"
  | "inherit"
  | "initial"
  | "unset";
export type Grow = number | "inherit" | "initial" | "unset";
export type Order = number | "inherit" | "initial" | "unset";
export type Shrink = number | "inherit" | "initial" | "unset";
export type Element =
  | "ul"
  | "aside"
  | "div"
  | "footer"
  | "header"
  | "main"
  | "nav"
  | "section"
  | "dt"
  | "dd"
  | "li"
  | ReactElement;

export type Layout = {
  align?: FLAlign;
  direction?: Direction;
  justify?: Justify;
  wrap?: Wrap;
};

export interface FlexLayoutItemProps {
  align?: Align;
  basis?: Basis;
  children?: ReactNode;
  className?: string;
  grow?: Grow;
  order?: Order;
  shrink?: Shrink;
  style?: CSSProperties;
  element?: Element;
  layout?: Layout;
  dangerouslySetInnerHTML?: { __html: string };
  onClick?: () => void;
  tabIndex?: number;
  onKeyPress?: (event: KeyboardEvent<HTMLElement>) => void;
}

const FlexLayoutItem = forwardRef<HTMLElement, FlexLayoutItemProps>(
  (
    {
      align = "auto",
      basis = "auto",
      grow = 0,
      order = 0,
      shrink = 1,
      element = "div",
      children,
      className,
      style,
      layout,
      dangerouslySetInnerHTML,
      onClick,
      tabIndex,
      onKeyPress,
    },
    ref
  ) => {
    const computedClassName = cx(
      layout ? flexLayout({ ...flexLayout.defaults, ...layout }) : null,
      {
        [styles.alignAuto]: align === "auto",
        [styles.alignStart]: align === "start",
        [styles.alignCenter]: align === "center",
        [styles.alignEnd]: align === "end",
        [styles.alignStretch]: align === "stretch",
      },
      className
    );

    const computedStyle: CSSProperties = {
      ...style,
      flexBasis: basis,
      flexGrow: grow,
      flexShrink: shrink,
      order,
    };

    const elementProps = {
      className: computedClassName,
      style: computedStyle,
      dangerouslySetInnerHTML,
      onClick,
      ref,
      tabIndex,
      onKeyPress,
      ...(dangerouslySetInnerHTML ? {} : { children }),
    };

    return typeof element === "string"
      ? createElement(element, elementProps)
      : cloneElement(element, elementProps);
  }
);

FlexLayoutItem.displayName = "FlexLayoutItem";
export default FlexLayoutItem;
export const layouts = flexLayoutLayouts;
